/**
 * @file badge.tsx
 * @description Бейдж (chip) — статусы, метки (phase08.5).
 */

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  // 12/600, обычный регистр: капс в админке остался только у `.label-editorial`
  // (phase19.24, UI-контракт §1.2).
  "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold whitespace-nowrap [&_svg]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-surface-low text-on-surface",
        primary: "bg-primary/12 text-primary",
        secondary: "bg-secondary text-secondary-foreground",
        accent: "bg-accent text-accent-foreground",
        success: "bg-success/12 text-success",
        warning: "bg-warning/15 text-warning",
        destructive: "bg-destructive/12 text-destructive",
        // phase19.29: тона статусов «в работе», «особое», «неактивно» (в теме
        // Commora — пилюли с точкой; в теме магазина пока не используются).
        info: "bg-info/12 text-info",
        special: "bg-primary-end/12 text-primary-end",
        neutral: "bg-surface-low text-on-surface-variant",
        outline: "[box-shadow:inset_0_0_0_1px_var(--outline-variant)] text-on-surface",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <span
      data-slot="badge"
      // Вариант — в атрибут: по нему тема Commora рисует «пилюлю» с точкой
      // (index.css), компонент о теме не знает.
      data-variant={variant ?? "default"}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { badgeVariants };
